import { lazy } from 'react'
import { Routes, Route } from 'react-router-dom'
import ScrollToTop from './components/ScrollToTop'
import Layout from './components/Layout'
import AdminLayout from './components/AdminLayout'
import { AuthProvider } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import LanguageRouter from './components/LanguageRouter'

const Home = lazy(() => import('./pages/Home'))
const Blog = lazy(() => import('./pages/Blog'))
const Store = lazy(() => import('./pages/Store'))
const PostDetail = lazy(() => import('./pages/PostDetail'))
const PostsList = lazy(() => import('./pages/PostsList'))
const PostEditor = lazy(() => import('./pages/PostEditor'))
const PagesList = lazy(() => import('./pages/PagesList'))
const PageEditor = lazy(() => import('./pages/PageEditor'))
const PageDetail = lazy(() => import('./pages/PageDetail'))
const BlocksList = lazy(() => import('./pages/BlocksList'))
const BlockEditor = lazy(() => import('./pages/BlockEditor'))
const MenusList = lazy(() => import('./pages/MenusList'))
const MenuEditor = lazy(() => import('./pages/MenuEditor'))
const SliderList = lazy(() => import('./pages/SliderList'))
const SliderEditor = lazy(() => import('./pages/SliderEditor'))
const CategoriesList = lazy(() => import('./pages/CategoriesList'))
const CategoryEditor = lazy(() => import('./pages/CategoryEditor'))
const ProductsList = lazy(() => import('./pages/ProductsList'))
const ProductEditor = lazy(() => import('./pages/ProductEditor'))
const VariationEditor = lazy(() => import('./pages/VariationEditor'))
const ProductCategoriesList = lazy(() => import('./pages/ProductCategoriesList'))
const ProductCategoryEditor = lazy(() => import('./pages/ProductCategoryEditor'))
const ProductDetail = lazy(() => import('./pages/ProductDetail'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Settings = lazy(() => import('./pages/Settings'))
import Sitemap from './pages/Sitemap'
const UsersList = lazy(() => import('./pages/UsersList'))
const UserEdit = lazy(() => import('./pages/UserEdit'))
const EmailTemplatesList = lazy(() => import('./pages/EmailTemplatesList'))
const EmailTemplateEditor = lazy(() => import('./pages/EmailTemplateEditor'))
const AdminEmailQueue = lazy(() => import('./pages/AdminEmailQueue'))
const AdminEmailSettings = lazy(() => import('./pages/AdminEmailSettings'))
const AdminSubscribers = lazy(() => import('./pages/AdminSubscribers'))
const AdminCampaigns = lazy(() => import('./pages/AdminCampaigns'))
const AdminCampaignEditor = lazy(() => import('./pages/AdminCampaignEditor'))
const AdminNewsletterTemplates = lazy(() => import('./pages/AdminNewsletterTemplates'))
const AdminNewsletterTemplateEditor = lazy(() => import('./pages/AdminNewsletterTemplateEditor'))
const Cart = lazy(() => import('./pages/Cart'))
const Checkout = lazy(() => import('./pages/Checkout'))
const CheckoutPayment = lazy(() => import('./pages/CheckoutPayment'))
const OrderDetail = lazy(() => import('./pages/OrderDetail'))
const MyOrders = lazy(() => import('./pages/MyOrders'))
const MySubscriptions = lazy(() => import('./pages/MySubscriptions'))
const UserDashboard = lazy(() => import('./pages/UserDashboard'))
const Addresses = lazy(() => import('./pages/Addresses'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const AdminOrders = lazy(() => import('./pages/AdminOrders'))
const AdminOrderDetail = lazy(() => import('./pages/AdminOrderDetail'))
const AdminSubscriptions = lazy(() => import('./pages/AdminSubscriptions'))
const AdminSubscriptionDetail = lazy(() => import('./pages/AdminSubscriptionDetail'))
const AdminPaymentMethods = lazy(() => import('./pages/AdminPaymentMethods'))
const FormsList = lazy(() => import('./pages/FormsList'))
const SendsList = lazy(() => import('./pages/SendsList'))
const SendsDetail = lazy(() => import('./pages/SendsDetail'))
const NotificationsList = lazy(() => import('./pages/NotificationsList'))
const PublicForm = lazy(() => import('./pages/PublicForm'))
const UserTickets = lazy(() => import('./pages/UserTickets'))
const UserTicketDetail = lazy(() => import('./pages/UserTicketDetail'))
const AdminTickets = lazy(() => import('./pages/AdminTickets'))
const AdminTicketDetail = lazy(() => import('./pages/AdminTicketDetail'))
const AdminApiKeys = lazy(() => import('./pages/AdminApiKeys'))
const AdminApiDocs = lazy(() => import('./pages/AdminApiDocs'))
const AdminChat = lazy(() => import('./pages/AdminChat'))
const ChatSettings = lazy(() => import('./pages/ChatSettings'))

const publicRouteChildren = (
  <>
    <Route index element={<Home />} />
    <Route path="blog" element={<Blog />} />
    <Route path="store" element={<Store />} />
    <Route path="post/:slug" element={<PostDetail />} />
    <Route path="product/:slug" element={<ProductDetail />} />
    <Route path="page/:slug" element={<PageDetail />} />

    <Route path="login" element={<Login />} />
    <Route path="register" element={<Register />} />
    <Route path="forgot-password" element={<ForgotPassword />} />
    <Route path="reset-password" element={<ResetPassword />} />

    <Route path="cart" element={<Cart />} />
    <Route path="checkout" element={<Checkout />} />
    <Route path="checkout/payment/:id" element={<CheckoutPayment />} />
    <Route path="order/:id" element={<OrderDetail />} />
    <Route path="orders" element={<MyOrders />} />
    <Route path="subscriptions" element={<MySubscriptions />} />

    <Route path="dashboard" element={<UserDashboard />} />
    <Route path="addresses" element={<Addresses />} />
    <Route path="tickets" element={<UserTickets />} />
    <Route path="tickets/new" element={<UserTicketDetail />} />
    <Route path="tickets/:id" element={<UserTicketDetail />} />

    <Route path="settings" element={<Settings />} />

    <Route path="form/:slug" element={<PublicForm />} />
  </>
)

const adminRouteChildren = (
  <>
    <Route index element={<AdminDashboard />} />
    <Route path="posts" element={<PostsList />} />
    <Route path="posts/new" element={<PostEditor />} />
    <Route path="posts/edit/:id" element={<PostEditor />} />
    <Route path="pages" element={<PagesList />} />
    <Route path="pages/new" element={<PageEditor />} />
    <Route path="pages/edit/:id" element={<PageEditor />} />
    <Route path="blocks" element={<BlocksList />} />
    <Route path="blocks/new" element={<BlockEditor />} />
    <Route path="blocks/edit/:id" element={<BlockEditor />} />
    <Route path="menus/:menuType" element={<MenusList />} />
    <Route path="menus/:menuType/new" element={<MenuEditor />} />
    <Route path="menus/:menuType/edit/:id" element={<MenuEditor />} />
    <Route path="slider" element={<SliderList />} />
    <Route path="slider/new" element={<SliderEditor />} />
    <Route path="slider/edit/:id" element={<SliderEditor />} />
    <Route path="users" element={<UsersList />} />
    <Route path="users/edit/:id" element={<UserEdit />} />
    <Route path="email-templates" element={<EmailTemplatesList />} />
    <Route path="email-templates/edit/:id" element={<EmailTemplateEditor />} />
    <Route path="email-queue" element={<AdminEmailQueue />} />
    <Route path="email-settings" element={<AdminEmailSettings />} />
    <Route path="subscribers" element={<AdminSubscribers />} />
    <Route path="newsletter/campaigns" element={<AdminCampaigns />} />
    <Route path="newsletter/campaigns/new" element={<AdminCampaignEditor />} />
    <Route path="newsletter/campaigns/edit/:id" element={<AdminCampaignEditor />} />
    <Route path="newsletter/templates" element={<AdminNewsletterTemplates />} />
    <Route path="newsletter/templates/new" element={<AdminNewsletterTemplateEditor />} />
    <Route path="newsletter/templates/edit/:id" element={<AdminNewsletterTemplateEditor />} />
    <Route path="categories" element={<CategoriesList />} />
    <Route path="categories/new" element={<CategoryEditor />} />
    <Route path="categories/edit/:id" element={<CategoryEditor />} />
    <Route path="products" element={<ProductsList />} />
    <Route path="products/new" element={<ProductEditor />} />
    <Route path="products/edit/:id" element={<ProductEditor />} />
    <Route path="products/edit/:productId/variation/:variationIndex" element={<VariationEditor />} />
    <Route path="products/categories" element={<ProductCategoriesList />} />
    <Route path="products/categories/new" element={<ProductCategoryEditor />} />
    <Route path="products/categories/edit/:id" element={<ProductCategoryEditor />} />
    <Route path="orders" element={<AdminOrders />} />
    <Route path="orders/:id" element={<AdminOrderDetail />} />
    <Route path="subscriptions" element={<AdminSubscriptions />} />
    <Route path="subscriptions/:id" element={<AdminSubscriptionDetail />} />
    <Route path="payment-methods" element={<AdminPaymentMethods />} />
    <Route path="forms" element={<FormsList />} />
    <Route path="sends" element={<SendsList />} />
    <Route path="sends/:id" element={<SendsDetail />} />
    <Route path="notifications" element={<NotificationsList />} />
    <Route path="tickets" element={<AdminTickets />} />
    <Route path="tickets/:id" element={<AdminTicketDetail />} />
    <Route path="api-keys" element={<AdminApiKeys />} />
    <Route path="api-keys/docs" element={<AdminApiDocs />} />
    <Route path="chat" element={<AdminChat />} />
    <Route path="chat/:conversationId" element={<AdminChat />} />
    <Route path="chat/settings" element={<ChatSettings />} />
  </>
)

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <ScrollToTop />
      <Routes>
        {/* Sitemap — no Layout, no lang prefix */}
        <Route path="/sitemap.xml" element={<Sitemap />} />

        {/* Public routes — default (no lang prefix, English) */}
        <Route path="/" element={<Layout />}>
          <Route element={<LanguageRouter />}>
            {publicRouteChildren}
          </Route>
        </Route>

        {/* Public routes — with lang prefix */}
        <Route path="/:lang" element={<Layout />}>
          {publicRouteChildren}
        </Route>

        {/* Admin routes — default (no lang prefix) */}
        <Route path="/admin" element={<AdminLayout />}>
          {adminRouteChildren}
        </Route>

        {/* Admin routes — with lang prefix */}
        <Route path="/:lang/admin" element={<AdminLayout />}>
          {adminRouteChildren}
        </Route>
      </Routes>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
